const DEFAULT_GRAVATAR_SIZE = 80
const UINT32_SIZE = 0x100000000
const textEncoder = new TextEncoder()

const MD5_K = new Int32Array([
  -680876936, -389564586, 606105819, -1044525330, -176418897, 1200080426, -1473231341, -45705983,
  1770035416, -1958414417, -42063, -1990404162, 1804603682, -40341101, -1502002290, 1236535329,
  -165796510, -1069501632, 643717713, -373897302, -701558691, 38016083, -660478335, -405537848,
  568446438, -1019803690, -187363961, 1163531501, -1444681467, -51403784, 1735328473, -1926607734,
  -378558, -2022574463, 1839030562, -35309556, -1530992060, 1272893353, -155497632, -1094730640,
  681279174, -358537222, -722521979, 76029189, -640364487, -421815835, 530742520, -995338651,
  -198630844, 1126891415, -1416354905, -57434055, 1700485571, -1894986606, -1051523, -2054922799,
  1873313359, -30611744, -1560198380, 1309151649, -145523070, -1120210379, 718787259, -343485551,
])

const MD5_S = [
  7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14,
  20, 5, 9, 14, 20, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 6, 10, 15, 21, 6,
  10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21,
]

function rotateLeft(value: number, shift: number): number {
  return (value << shift) | (value >>> (32 - shift))
}

function toInt32(value: number): number {
  return Math.imul(value, 1)
}

function toMd5Words(bytes: Uint8Array): number[] {
  const words: number[] = []
  for (let i = 0; i < bytes.length; i += 4) {
    words[i >> 2] = bytes[i] | (bytes[i + 1] << 8) | (bytes[i + 2] << 16) | (bytes[i + 3] << 24)
  }
  return words
}

interface Md5State {
  a: number
  b: number
  c: number
  d: number
}

function createPaddedMd5Input(input: string): Uint8Array {
  const utf8 = textEncoder.encode(input)
  const originalLength = utf8.length
  const bitLength = originalLength * 8
  const paddedLength = Math.ceil((originalLength + 9) / 64) * 64
  const padded = new Uint8Array(paddedLength)
  padded.set(utf8)
  padded[originalLength] = 0x80

  const view = new DataView(padded.buffer)
  view.setUint32(paddedLength - 8, bitLength >>> 0, true)
  view.setUint32(paddedLength - 4, Math.floor(bitLength / UINT32_SIZE) >>> 0, true)

  return padded
}

function getMd5RoundValues(
  i: number,
  bb: number,
  cc: number,
  dd: number,
): { f: number; g: number } {
  if (i < 16) {
    return { f: (bb & cc) | (~bb & dd), g: i }
  }
  if (i < 32) {
    return { f: (dd & bb) | (~dd & cc), g: (5 * i + 1) % 16 }
  }
  if (i < 48) {
    return { f: bb ^ cc ^ dd, g: (3 * i + 5) % 16 }
  }

  return { f: cc ^ (bb | ~dd), g: (7 * i) % 16 }
}

function processMd5Block(state: Md5State, bytes: Uint8Array): Md5State {
  const chunk = toMd5Words(bytes)
  let aa = state.a
  let bb = state.b
  let cc = state.c
  let dd = state.d

  for (let i = 0; i < 64; i++) {
    const { f, g } = getMd5RoundValues(i, bb, cc, dd)
    const temp = dd
    dd = cc
    cc = bb
    const sum = toInt32(aa + f + MD5_K[i] + chunk[g])
    bb = toInt32(bb + rotateLeft(sum, MD5_S[i]))
    aa = temp
  }

  return {
    a: toInt32(state.a + aa),
    b: toInt32(state.b + bb),
    c: toInt32(state.c + cc),
    d: toInt32(state.d + dd),
  }
}

function md5StateToHex(state: Md5State): string {
  const result = new DataView(new ArrayBuffer(16))
  result.setUint32(0, state.a, true)
  result.setUint32(4, state.b, true)
  result.setUint32(8, state.c, true)
  result.setUint32(12, state.d, true)

  return Array.from(new Uint8Array(result.buffer), byte => byte.toString(16).padStart(2, "0")).join(
    "",
  )
}

/** MD5 for Gravatar email hashes only (normalized emails are far below 2^32 bits). */
export function md5EmailHash(input: string): string {
  const padded = createPaddedMd5Input(input)
  let state: Md5State = {
    a: 0x67452301,
    b: 0xefcdab89,
    c: 0x98badcfe,
    d: 0x10325476,
  }

  for (let offset = 0; offset < padded.length; offset += 64) {
    state = processMd5Block(state, padded.subarray(offset, offset + 64))
  }

  return md5StateToHex(state)
}

export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase()
}

export function getGravatarHash(email: string): string {
  return md5EmailHash(normalizeEmail(email))
}

export function getGravatarUrlFromHash(hash: string, size = DEFAULT_GRAVATAR_SIZE): string {
  return `https://www.gravatar.com/avatar/${hash}?s=${size}&d=404`
}

export function getGravatarUrl(email: string, size = DEFAULT_GRAVATAR_SIZE): string {
  return getGravatarUrlFromHash(getGravatarHash(email), size)
}

export function resolveUserAvatarUrl(opts: {
  image?: string | null
  email?: string | null
  size?: number
}): string | undefined {
  const image = opts.image?.trim()
  if (image) return image

  const email = opts.email?.trim()
  if (!email) return undefined

  return getGravatarUrl(email, opts.size ?? DEFAULT_GRAVATAR_SIZE)
}

export function getCurrentUserAvatarProxyUrl(size = DEFAULT_GRAVATAR_SIZE): string {
  return `/api/v1/users/me/avatar?s=${size}`
}

export function getUserAvatarProxyUrl(userId: string, size = DEFAULT_GRAVATAR_SIZE): string {
  return `/api/v1/users/${encodeURIComponent(userId)}/avatar?s=${size}`
}
